// Error types
export interface AppError extends Error {
  code?: string;
  status?: number;
  details?: {
    errors?: Record<string, string[]>;
    originalError?: any;
  };
}

// Function to create a standardized error
export function createAppError(
  message: string,
  code?: string,
  status?: number,
  details?: AppError['details']
): AppError {
  const error = new Error(message) as AppError;
  error.code = code;
  error.status = status;
  error.details = details;
  return error;
}

// Function to handle API errors
export function handleApiError(error: any): AppError {
  if (error instanceof Error) {
    return createAppError(
      error.message,
      'API_ERROR',
      500,
      { originalError: error }
    );
  }
  
  return createAppError(
    'An unexpected error occurred',
    'UNKNOWN_ERROR',
    500,
    { originalError: error }
  );
}

// Function to handle Supabase errors
export function handleSupabaseError(error: any): AppError {
  if (error?.code === 'PGRST116') {
    return createAppError(
      'No data found',
      'NOT_FOUND',
      404,
      { originalError: error }
    );
  }

  if (error?.code === 'auth/invalid-email') {
    return createAppError(
      'Invalid email address',
      'INVALID_EMAIL',
      400,
      { originalError: error }
    );
  }

  if (error?.message?.includes('auth/')) {
    return createAppError(
      'Authentication error',
      'AUTH_ERROR',
      401,
      { originalError: error }
    );
  }

  return createAppError(
    error?.message || 'Database error',
    'DB_ERROR',
    500,
    { originalError: error }
  );
}

// Function to handle validation errors
export function handleValidationError(errors: Record<string, string[]>): AppError {
  return createAppError(
    'Validation failed',
    'VALIDATION_ERROR',
    400,
    { errors }
  );
}

// Function to format error messages for display
export function formatErrorMessage(error: AppError): string {
  if (error.code === 'VALIDATION_ERROR' && error.details?.errors) {
    return Object.entries(error.details.errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('\n');
  }

  return error.message;
} 